import { useI18n } from 'vue-i18n';

import type {
  Luggage,
  LuggageFromAccounts,
  LuggageUnit,
  SegmentLuggage,
} from '../types';
import { UNITS } from '../config';
import ru from '../config/locales/ru.json';
import kk from '../config/locales/kk.json';
import en from '../config/locales/en.json';

interface ParsedLuggage {
  unit: LuggageUnit;
  value: number;
  count: number;
}

const missingLuggage: ParsedLuggage = {
  unit: 'KG',
  value: 0,
  count: 0,
};

/**
 * Отличает плоский багаж заказа от вложенного багажа выдачи.
 *
 * У формы из выдачи нормы лежат в `free` и разложены по типам пассажиров, у
 * формы из заказа они уже сведены в плоские `unit`/`value`/`factor`. Признаком
 * берём `unit` на верхнем уровне: в выдаче он есть только внутри `free`.
 */
export function isLuggageFromAccounts(
  luggage: SegmentLuggage | null | undefined,
): luggage is LuggageFromAccounts {
  return !!luggage && 'unit' in luggage;
}

/**
 * Норма ручной клади после конвертации PC в KG, если бэк её посчитал.
 *
 * Временное решение для ручной клади FS: с 1 марта 2026 норма выросла с 5 до
 * 7 кг, и до пользователя нужно доносить новое значение. Поле приходит только
 * в плоской форме из заказа, поэтому у багажа выдачи его нет.
 */
export function getConvertedLuggageValue(
  luggage: SegmentLuggage | null | undefined,
): number | undefined {
  return isLuggageFromAccounts(luggage) ? luggage.new_value : undefined;
}

export function parseLuggage(luggage: Luggage): ParsedLuggage {
  const availableBaggage = luggage.free?.ADT || luggage.free?.YTH;

  if (!availableBaggage || availableBaggage.value === 0) {
    return missingLuggage;
  }

  if (availableBaggage.unit === UNITS.PC) {
    return {
      unit: availableBaggage.unit,
      value: 'pc' in availableBaggage ? (availableBaggage.pc?.value ?? 0) : 0,
      count: availableBaggage.value,
    };
  }

  if (availableBaggage.unit === UNITS.KG) {
    return {
      unit: availableBaggage.unit,
      value: availableBaggage.value,
      count: 1,
    };
  }

  return missingLuggage;
}

export function luggageToStringFromAccounts(
  luggage: LuggageFromAccounts,
): string {
  const { t } = useI18n({ messages: { ru, kk, en } });

  if (luggage.value < 1) {
    return '';
  }

  if (luggage.unit === UNITS.PC && luggage.pc) {
    if (luggage.value > 1) {
      return `${luggage.value} x ${luggage.pc.value} ${t(
        `luggage.unit_${luggage.pc.unit}`,
      )}`;
    }

    return `${luggage.pc.value} ${t(`luggage.unit_${luggage.pc.unit}`)}`;
  }

  const factor = luggage.factor > 1 ? `${luggage.factor} x` : '';
  const translatedUnit = t(`luggage.unit_${luggage.unit}`);

  return [factor, luggage.value, translatedUnit].join(' ');
}

export function luggageToString({ luggage }: { luggage: Luggage }): string {
  const { t } = useI18n({ messages: { ru, kk, en } });
  const { count, value, unit } = parseLuggage(luggage);

  if (count <= 0) {
    return '';
  }

  const isKG = value > 0;
  const unitType = isKG ? UNITS.KG : UNITS.PC;
  const translatedUnit = t(`luggage.unit_${unitType}`);
  const shouldDisplayCount = unit === UNITS.PC || count > 1;

  if (isKG) {
    return shouldDisplayCount
      ? `${count} x ${value} ${translatedUnit}`
      : `${value} ${translatedUnit}`;
  }

  return shouldDisplayCount ? `${count} ${translatedUnit}` : translatedUnit;
}
